import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToastNotify } from '@opswat/ui';
import { b64DecodeUnicode, clearRedirectCookie, generateFingerPrint } from 'services/authenticator';
import { Apikey, Organization, Sso, User } from 'services/interface';
import { getCaptchaToken } from 'services/captcha';
import {
    axios,
    Fetcher,
    MutationFetcher,
    SWRConfiguration,
    SWRKey,
    useSWRImmutable,
    useSWRMutation,
} from 'services/swr';
import { useUser } from 'services/User';
import { useCswgProxyAccess } from 'services/api/CswgProxy';

interface CSRFHeader {
    token: string;
    sso: Sso;
}

export const useCSRFHeader = (options?: SWRConfiguration<CSRFHeader | undefined>) => {
    const { t } = useTranslation();
    const { toastNotify } = useToastNotify();
    const { setUser } = useUser();

    const csrfKey: SWRKey = { url: '/api/variables', baseURL: process.env.GATSBY_SITE_URL, maxRedirects: 0 };

    const csrfHeaderFetcher: Fetcher<CSRFHeader | undefined, SWRKey> = async ({ url, ...config }) =>
        axios
            .get(url, config)
            .then((response) => {
                const token: string = response.data.match(/METADEFENDER_TOKEN='(.*?)'/)[1];
                const ssoData = response.data.match(/SSO='(.*?)'/)?.[1];
                const sso: Sso = ssoData && JSON.parse(b64DecodeUnicode(ssoData));

                if (sso) {
                    setUser((prev) => ({ ...prev, ...sso, isLoggedIn: !!sso?.apikey }));
                } else {
                    setUser((prev) => ({ ...prev, isLoggedIn: false }));
                }

                return { token, sso };
            })
            .catch((error) => {
                setUser((prev) => ({ ...prev, isLoggedIn: false }));

                if (error?.message !== 'Request aborted') {
                    toastNotify({
                        title: t('Error'),
                        message: t('X-Csrf token not found'),
                        type: 'error',
                        hasClose: true,
                        duration: 4000,
                    });
                }

                throw error;
            })
            .finally(() => {
                clearRedirectCookie();
            });

    return useSWRImmutable(csrfKey, csrfHeaderFetcher, options);
};

export const useCreateApikey = () => {
    const apikeyKey: SWRKey = { url: '/api/token/', baseURL: process.env.GATSBY_SITE_URL };

    const createApikey: MutationFetcher<{ key: string }, SWRKey, { token: string; apikey: string }> = async (
        { url, ...config },
        { arg: data },
    ) => {
        const { token, apikey } = data;
        const apikeyUrl = url + apikey;
        const captchaToken = await getCaptchaToken();
        const headers = {
            ...config.headers,
            'X-Csrf': token,
            'x-captcha-token': captchaToken,
        };

        return axios
            .post(apikeyUrl, {}, { ...config, headers })
            .then((res) => res.data)
            .catch((error) => {
                // Apikey already exists
                if (error.response?.data?.code === 400020) {
                    return { key: apikey };
                }

                throw error;
            });
    };

    return useSWRMutation(apikeyKey, createApikey, { revalidate: false });
};

export const useApikeyData = (options?: SWRConfiguration<Apikey>) => {
    const { t } = useTranslation();
    const { toastNotify } = useToastNotify();
    const { setUser } = useUser();
    const { data: csrfData } = useCSRFHeader({ shouldRetryOnError: false });
    const { trigger, isMutating } = useCreateApikey();
    const [fingerprint, setFingerprint] = useState<string | null>(null);

    useEffect(() => {
        (async () => {
            try {
                const fp = await generateFingerPrint();
                setFingerprint(fp);
            } catch {
                // Do nothing
            }
        })();
    }, []);

    const userApikey = csrfData?.sso?.apikey ?? fingerprint;

    const apikeyKey: SWRKey = useMemo(() => {
        if (csrfData?.token && userApikey) {
            return {
                url: '/api/token/apikey',
                baseURL: process.env.GATSBY_SITE_URL,
                headers: {
                    'X-Csrf': csrfData.token,
                },
                data: {
                    apikey: userApikey,
                },
            };
        }

        return null;
    }, [csrfData, userApikey]);

    const apikeyFetcher: Fetcher<Apikey, SWRKey> = async ({ url, data, ...config }) =>
        axios.post(url, data, config).then((res) => {
            setUser((prev) => ({ ...prev, ...res.data }));
            return res.data;
        });

    const hookData = useSWRImmutable<Apikey>(apikeyKey, apikeyFetcher, {
        errorRetryCount: 1,
        onErrorRetry: (error, _key, _config, revalidate, { retryCount }) => {
            const isNotFound = [401, 404].includes(error.response?.status) || error.response?.data?.code === 401000;

            // Fire-and-forget: onErrorRetry expects a void return, so run the async
            // retry in a discarded IIFE rather than returning a Promise.
            void (async () => {
                if (csrfData?.token && userApikey && isNotFound && !isMutating) {
                    try {
                        await trigger({ token: csrfData.token, apikey: userApikey });
                        await revalidate({ retryCount });
                    } catch {
                        toastNotify({
                            title: t('Error'),
                            message: t('Failed to create API key'),
                            type: 'error',
                            hasClose: true,
                            duration: 4000,
                        });
                    }
                } else if (!isMutating) {
                    toastNotify({
                        title: t('Error'),
                        message: t('Failed to create API key'),
                        type: 'error',
                        hasClose: true,
                        duration: 4000,
                    });
                }
            })();
        },
        ...options,
    });

    return { ...hookData, isMutating };
};

export const useSetApiKeyData = () => {
    const cacheKey: SWRKey = { url: '/api/token', baseURL: process.env.GATSBY_SITE_URL };

    const updateApiKey: MutationFetcher<string, SWRKey, Apikey> = async ({ url, ...config }, { arg: payload }) => {
        return axios.put(url, payload, config).then((res) => res.data);
    };

    return useSWRMutation(cacheKey, updateApiKey, { revalidate: false });
};

export const useSetOrganizationData = () => {
    const cacheKey: SWRKey = { url: '/api/organization', baseURL: process.env.GATSBY_SITE_URL };

    const updateOrganization: MutationFetcher<string, SWRKey, Organization> = async (
        { url, ...config },
        { arg: payload },
    ) => {
        return axios.put(url, payload, config).then((res) => res.data);
    };

    return useSWRMutation(cacheKey, updateOrganization, { revalidate: false });
};

export const useUserData = (options?: SWRConfiguration<User>) => {
    // Probe CSWG proxy access once a session exists (config GET ok => tenant admin);
    // this enriches the shared user context so gating (tab + `/cswg` route) is BE-driven.
    useCswgProxyAccess();

    const {
        data: csrfData,
        isLoading: isLoadingCsrfData,
        isValidating: isValidatingCsrfData,
    } = useCSRFHeader({ shouldRetryOnError: false });
    const {
        data: apikeyData,
        isLoading: isLoadingApikeyData,
        isValidating: isValidatingApikeyData,
        isMutating,
        ...hookData
    } = useApikeyData(options);

    const data: User = {
        ...csrfData?.sso,
        ...apikeyData,
        isLoggedIn: Boolean(csrfData?.sso?.email),
    };

    const isLoading = isLoadingCsrfData || isLoadingApikeyData || isMutating;
    const isValidating = isValidatingCsrfData || isValidatingApikeyData;

    return { ...hookData, data, isLoading, isValidating };
};
